/**
 * todayPriority.js — the residence's priority engine.
 *
 * Given the current state of the house, this returns a small, ranked list
 * of what most needs attention right now. The intent is not scheduling —
 * it is *judgement*. The engine reads across every module and answers
 * one question:
 *
 *   "What is the most helpful thing the household needs right now?"
 *
 * All scoring is transparent, deterministic and offline. This is the
 * behavioural spine that every future intelligent feature will hook into.
 */

import { getDocumentsDueSoon, getDocumentsNeedingAction, isOverdue as docOverdue } from "./documentUtils";
import { getLowStockItems, getOutItems, getUseSoonItems, getExpiredItems } from "./suppliesUtils";

/* ─── helpers ────────────────────────────────────────────────────── */

const startOfDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const daysBetween = (a, b) => Math.round((startOfDay(b) - startOfDay(a)) / 86400000);

const parseDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const softName = (assignee, members = []) => {
  if (!assignee) return null;
  const m = members.find(x => x.id === assignee || x.name === assignee);
  return m ? m.name.split(' ')[0] : (typeof assignee === 'string' ? assignee.split(' ')[0] : null);
};

const relativeDueLabel = (dueDate, now = new Date()) => {
  const d = parseDate(dueDate);
  if (!d) return null;
  const delta = daysBetween(now, d);
  if (delta < -1) return `overdue by ${Math.abs(delta)} days`;
  if (delta === -1) return 'overdue by a day';
  if (delta === 0) return 'today';
  if (delta === 1) return 'tomorrow';
  if (delta < 7) return `in ${delta} days`;
  if (delta < 14) return 'next week';
  return `${delta} days away`;
};

/* ─── scoring & item shape ───────────────────────────────────────── */

/*
   Every candidate item resolves to:
   {
     score,          // number — higher = more urgent
     module,         // 'health' | 'money' | 'meals' | 'documents' | 'home' | 'family' | 'goals' | 'pantry' | 'cleaning'
     moduleLabel,    // 'Health' | 'Money' | ...  (label shown next to the room dot)
     time,           // 'Tue' | '10:00' | null    (optional leading time)
     title,          // Fraunces headline for the item
     meta,           // Inter meta line beneath
     roomLink,       // path to open the room
     source,         // 'document' | 'supply' | 'task' | ... — for debugging
   }
*/

function buildDocumentItems(documents, tasks) {
  const items = [];
  const needsAction = getDocumentsNeedingAction(documents);
  const dueSoon = getDocumentsDueSoon(documents, 14);

  for (const doc of needsAction) {
    const overdue = docOverdue(doc);
    items.push({
      score: overdue ? 32 : 28,
      module: 'documents',
      moduleLabel: 'Documents',
      time: null,
      title: doc.title,
      meta: overdue
        ? `overdue · ${relativeDueLabel(doc.dueDate || doc.renewalDate)} · in the library`
        : `${doc.status?.toLowerCase() || 'needs a moment'} · in the library`,
      roomLink: '/home-os/documents',
      source: 'document',
    });
  }
  for (const doc of dueSoon) {
    if (needsAction.some(d => d.id === doc.id)) continue;
    items.push({
      score: 18,
      module: 'documents',
      moduleLabel: 'Documents',
      time: null,
      title: doc.title,
      meta: `${relativeDueLabel(doc.dueDate || doc.renewalDate)} · in the library`,
      roomLink: '/home-os/documents',
      source: 'document',
    });
  }
  return items;
}

function buildSupplyItems(suppliesItems) {
  const items = [];
  const out = getOutItems(suppliesItems);
  const expired = getExpiredItems(suppliesItems);
  const low = getLowStockItems(suppliesItems);
  const useSoon = getUseSoonItems(suppliesItems);

  if (out.length > 0) {
    const names = out.slice(0, 3).map(i => i.name).join(', ');
    items.push({
      score: 24,
      module: 'meals',
      moduleLabel: 'Meals',
      time: null,
      title: out.length === 1
        ? `The kitchen has run out of ${out[0].name.toLowerCase()}`
        : `${out.length} kitchen provisions have run out`,
      meta: `${names}${out.length > 3 ? ' and more' : ''} · in the pantry`,
      roomLink: '/home-os/supplies',
      source: 'supply-out',
    });
  }

  if (expired.length > 0) {
    items.push({
      score: 20,
      module: 'meals',
      moduleLabel: 'Meals',
      time: null,
      title: expired.length === 1
        ? `${expired[0].name} has quietly expired`
        : `${expired.length} pantry items have expired`,
      meta: expired.slice(0, 3).map(i => i.name).join(', ') + ' · worth clearing',
      roomLink: '/home-os/supplies',
      source: 'supply-expired',
    });
  }

  if (low.length > 0) {
    const names = low.slice(0, 3).map(i => i.name.toLowerCase()).join(', ');
    items.push({
      score: 14,
      module: 'meals',
      moduleLabel: 'Meals',
      time: null,
      title: low.length === 1
        ? `${low[0].name} is running low`
        : `Kitchen provisions running low`,
      meta: `${names}${low.length > 3 ? ' and more' : ''} · order suggested`,
      roomLink: '/home-os/supplies',
      source: 'supply-low',
    });
  }

  if (useSoon.length > 0) {
    items.push({
      score: 8,
      module: 'meals',
      moduleLabel: 'Meals',
      time: null,
      title: useSoon.length === 1
        ? `Use the ${useSoon[0].name.toLowerCase()} soon`
        : `${useSoon.length} items in the fridge want using`,
      meta: 'meal ideas in the kitchen',
      roomLink: '/home-os/meals',
      source: 'supply-use-soon',
    });
  }

  return items;
}

function buildCleaningItems(cleaningTasks = [], members = []) {
  const items = [];
  const now = new Date();
  for (const t of cleaningTasks) {
    if (t.status === 'done' || t.status === 'complete') continue;
    const due = parseDate(t.dueDate || t.scheduledDate);
    if (!due) continue;
    const delta = daysBetween(now, due);
    if (delta > 3) continue;

    const isOverdue = delta < 0;
    const isToday = delta === 0;
    const score = isOverdue ? 26 : isToday ? 16 : 10;
    const assignee = softName(t.assignedTo, members);

    items.push({
      score,
      module: 'home',
      moduleLabel: 'Home',
      time: isToday ? 'today' : isOverdue ? `${Math.abs(delta)}d ago` : `in ${delta}d`,
      title: t.title || t.name || 'A cleaning task',
      meta: [assignee, t.room, t.frequency].filter(Boolean).join(' · ') || (isOverdue ? 'gently overdue' : 'on the roster'),
      roomLink: '/home-os/cleaning',
      source: 'cleaning',
    });
  }
  return items;
}

function buildTaskItems(tasks = [], members = []) {
  const items = [];
  const now = new Date();
  for (const t of tasks) {
    if (t.status === 'done' || t.status === 'complete') continue;
    const due = parseDate(t.dueDate);
    if (!due) continue;
    const delta = daysBetween(now, due);
    if (delta > 7) continue;

    const isOverdue = delta < 0;
    const isToday = delta === 0;
    const module = moduleForTaskCategory(t.category);
    const score = isOverdue ? 22 : isToday ? 16 : 12;
    const assignee = softName(t.assignedTo, members);
    const dayLabel = isToday ? 'today' : isOverdue ? relativeDueLabel(due, now) : relativeDueLabel(due, now);

    items.push({
      score,
      module: module.key,
      moduleLabel: module.label,
      time: dayLabel,
      title: t.title || 'A household task',
      meta: [assignee, t.description].filter(Boolean).join(' · ') || (isOverdue ? 'gently overdue' : 'on the plan'),
      roomLink: module.link,
      source: 'task',
      completion: { kind: 'task', id: t.id, assignee },
    });
  }
  return items;
}

/* Bills the Study has flagged as "Due soon" — completing means marking paid
   in the existing money.bills slice (one source of truth). */
function buildBillItems(bills = []) {
  const items = [];
  for (const b of bills) {
    if (b.status !== 'Due soon') continue;
    items.push({
      score: 29,
      module: 'money',
      moduleLabel: 'Money',
      time: null,
      title: `Pay ${b.name || 'a bill'}`,
      meta: [b.amount, b.due, 'in the Study'].filter(Boolean).join(' · '),
      roomLink: '/home-os/money',
      source: 'bill',
      completion: { kind: 'bill', id: b.id },
    });
  }
  return items;
}

/* Tonight's dinner from the existing weekly meal plan. No completion —
   planning happens in the Kitchen. */
function buildDinnerItem(weeklyMealPlan = [], members = []) {
  if (!members.length) return [];
  const hour = new Date().getHours();
  if (hour >= 19) return [];
  const todayName = new Date().toLocaleDateString('en-NZ', { weekday: 'long' });
  const day = (weeklyMealPlan || []).find(d => d.day === todayName);
  const dinnerSlot = day?.slots?.dinner;
  const planned = Boolean(dinnerSlot?.title || dinnerSlot?.recipeId || day?.dinner || day?.dinnerRecipeId);
  if (planned) return [];
  return [{
    score: 12,
    module: 'meals',
    moduleLabel: 'Meals',
    time: null,
    title: 'Plan dinner',
    meta: 'nothing is planned for tonight yet',
    roomLink: '/home-os/meals',
    source: 'dinner',
  }];
}

function buildMoodItems(moodCheckins = [], members = []) {
  const items = [];
  if (!moodCheckins.length) return items;
  const recent = moodCheckins.slice(0, 6);
  const lowMoods = recent.filter(c => ['low', 'sad', 'anxious', 'tired', 'stressed'].includes((c.mood || '').toLowerCase()));
  if (lowMoods.length === 0) return items;
  const who = lowMoods[0].memberId;
  const name = softName(who, members);
  items.push({
    score: 14,
    module: 'health',
    moduleLabel: 'Health',
    time: null,
    title: name ? `${name} could use a soft moment` : 'The family mood has dipped',
    meta: 'recent check-in · in the wellness room',
    roomLink: '/home-os/health',
    source: 'mood',
  });
  return items;
}

function moduleForTaskCategory(category = '') {
  const c = String(category).toLowerCase();
  if (c.includes('chore'))                                              return { key: 'family',    label: 'Chores',    link: '/home-os/chores' };
  if (c.includes('meal') || c.includes('food') || c.includes('cook')) return { key: 'meals',     label: 'Meals',     link: '/home-os/meals' };
  if (c.includes('money') || c.includes('bill') || c.includes('bank')) return { key: 'money',     label: 'Money',     link: '/home-os/money' };
  if (c.includes('health') || c.includes('doctor'))                     return { key: 'health',    label: 'Health',    link: '/home-os/health' };
  if (c.includes('doc') || c.includes('paper'))                         return { key: 'documents', label: 'Documents', link: '/home-os/documents' };
  if (c.includes('clean') || c.includes('maintain') || c.includes('home')) return { key: 'home',   label: 'Home',      link: '/home-os/maintenance' };
  if (c.includes('family') || c.includes('kid') || c.includes('school')) return { key: 'family',   label: 'Family',    link: '/home-os/family' };
  if (c.includes('goal') || c.includes('plan'))                         return { key: 'goals',     label: 'Goals',     link: '/home-os/family-plan' };
  return { key: 'today', label: 'Today', link: '/home-os' };
}

/* ─── the engine ─────────────────────────────────────────────────── */

export function prioritiseToday({
  documents = [],
  tasks = [],
  cleaningTasks = [],
  suppliesItems = [],
  moodCheckins = [],
  members = [],
  bills = [],
  weeklyMealPlan = [],
} = {}) {
  const all = [
    ...buildDocumentItems(documents, tasks),
    ...buildSupplyItems(suppliesItems),
    ...buildCleaningItems(cleaningTasks, members),
    ...buildTaskItems(tasks, members),
    ...buildMoodItems(moodCheckins, members),
    ...buildBillItems(bills),
    ...buildDinnerItem(weeklyMealPlan, members),
  ];

  const ranked = all.sort((a, b) => b.score - a.score);
  const topThree = ranked.slice(0, 3);
  const remaining = ranked.length - topThree.length;

  const highestScore = ranked[0]?.score || 0;
  const nothing = ranked.length === 0;

  return {
    items: topThree,
    remaining,
    total: ranked.length,
    intensity: nothing ? 'calm' : highestScore >= 25 ? 'urgent' : highestScore >= 15 ? 'attentive' : 'gentle',
  };
}

/* ─── copy composition ───────────────────────────────────────────── */

const countWord = (n) => (['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven'][n] || String(n));

export function composeHeadline({ items, intensity, resident }) {
  const n = items.length;
  const name = resident ? `, ${resident}` : '';

  if (n === 0) {
    return `The house is calm${name}. Nothing asks for you today.`;
  }

  const partA = ({
    gentle:    `The house is calm${name}.`,
    attentive: `The house holds a few matters${name}.`,
    urgent:    `The day is asking of you${name}.`,
  })[intensity] || `The house is calm${name}.`;

  const countLabel = countWord(n).charAt(0).toUpperCase() + countWord(n).slice(1);
  const matterWord = n === 1 ? 'matter' : 'matters';
  const askVerb    = intensity === 'urgent' ? 'ask' : 'quietly ask';
  return `${partA} ${countLabel} ${matterWord} ${askVerb} for you today.`;
}

export function composeStandfirst({ items, resident }) {
  if (items.length === 0) {
    return `Everything is in hand. The rooms are quiet${resident ? `, ${resident}` : ''} — no post, no deadlines, no dust waiting.`;
  }
  const clauses = items.map(it => it.title);
  const joined = clauses.length === 1
    ? clauses[0]
    : clauses.length === 2
      ? `${clauses[0]}, and ${clauses[1]}`
      : `${clauses.slice(0, -1).join(', ')}, and ${clauses[clauses.length - 1]}`;
  return `Right now: ${joined}. Everything else is in hand.`;
}

export function composeClosing({ items, remaining, intensity }) {
  if (items.length === 0) return 'The house is otherwise calm.';
  if (remaining > 0) return `And ${countWord(remaining)} smaller ${remaining === 1 ? 'matter waits' : 'matters wait'} in their rooms.`;
  if (intensity === 'gentle')    return 'The house is otherwise calm.';
  if (intensity === 'attentive') return 'The house is otherwise steady.';
  return 'Nothing else needs the family today.';
}

/* ─── household pulse composition ────────────────────────────────── */

export function composePulse({ suppliesItems = [], documents = [], members = [] }) {
  const items = [];

  // Family presence — up to 3 members shown; older kids and pets etc. drift into Family Room.
  const hour = new Date().getHours();
  const activeMembers = members.slice(0, 3);
  const workingHours = hour >= 8 && hour < 16;
  activeMembers.forEach((m, i) => {
    const away = workingHours && i % 2 === 0;
    items.push({
      label: `${m.name.split(' ')[0]} · ${away ? 'out' : 'home'}`,
      tone: 'family',
      live: !away,
    });
  });

  // Kitchen state
  const low = suppliesItems.filter(s => ['Low', 'Out'].includes(s.status)).length;
  items.push({
    label: low === 0 ? 'kitchen · quiet' : `kitchen · ${low} low`,
    tone: 'meals',
    live: false,
  });

  // House temperature — mock, but live
  items.push({
    label: '21° inside',
    tone: 'home',
    live: true,
  });

  return items.slice(0, 5);
}
